import { useMemo } from "react";
import { FormField } from "../form-field/FormField";
import { CheckboxField } from "~/components";
import cn from "classnames";
import { useAcceptPolitics } from "./AcceptPoliticsContext";
import styles from "./AcceptPolitics.module.scss";
import { URL_OFERTA, URL_TARIF } from "~/configs/consts";
import { OrgTypeEnum } from "~/typings/types";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { postToggleOfferCheckbox } from "~/pages/questionnaires/v2/customer/api/apiMethods";
import { reactNodeToText } from "./reactNodeToText";

const buildCustomerAcceptPoliticsTitle = (orgType?: OrgTypeEnum) => {
  const ofertaUrl = URL_OFERTA;
  return (
    <p className={styles.text}>
      Я ознакомлен(а) и согласен(на) в полном объеме с условиями предоставления услуг, положениями{" "}
      <a href={ofertaUrl} target="_blank" className={styles.textBlueLink} rel="noopener noreferrer">
        Оферты
      </a>
      ,{" "}
      {orgType === OrgTypeEnum.SelfEmployed && (
        <>
          <a href={URL_OFERTA} target="_blank" className={styles.textBlueLink} rel="noopener noreferrer">
            Оферты
          </a>
          ,{" "}
        </>
      )}
      <a href={URL_TARIF} target="_blank" className={styles.textBlueLink} rel="noopener noreferrer">
        Тарифов
      </a>{" "}
      и принимаю их. Я подтверждаю достоверность введенных данных.
    </p>
  );
};

const CustomerAcceptsPrivacyTitle = () => (
  <p className={styles.text}>
    Я даю{" "}
    <a
      href="https://prodamus.ru/soglasie-kontragenta-na-peredachu-pd-tretyim-licam"
      target="_blank"
      className={styles.textBlueLink}
      rel="noopener noreferrer"
    >
      согласие
    </a>{" "}
    на обработку (передачу) персональных данных.
  </p>
);

const CustomerAcceptPromoTitle = () => (
  <p className={styles.text}>
    Я даю{" "}
    <a href="https://prodamus.ru/soglasie-personalnye-dannye-i-consenttomailings" target="_blank" className={styles.textBlueLink} rel="noopener noreferrer">
      согласие
    </a>{" "}
    на получение рекламно-информационной рассылки и обработку персональных данных.
  </p>
);

interface Props {
  containerClassName?: string;
}

export const CustomerAcceptPolitics = ({ containerClassName }: Props) => {
  const {
    isAcceptPolitics, setIsAcceptPolitics, acceptPoliticsError, setAcceptPoliticsError,
    isAcceptPrivacy, setIsAcceptPrivacy, acceptPrivacyError, setAcceptPrivacyError,
    isAcceptPromo, setIsAcceptPromo, acceptPromoError, setAcceptPromoError,
    isPendingOffer, setIsPendingOffer,
    orgType, clientId,
  } = useAcceptPolitics();

  const politicsTitle = useMemo(() => buildCustomerAcceptPoliticsTitle(orgType), [orgType]);

  const handleAcceptPoliticsChange = async (val: boolean) => {
    if (val) setAcceptPoliticsError(null);
    setIsAcceptPolitics(val);

    if (!clientId) return;

    setIsPendingOffer(true);
    try {
      const ofertaUrl = URL_OFERTA;

      const checkboxText = reactNodeToText(politicsTitle);

      if (val) {
        const checkboxes = [
          {
            questionnaire_uid: clientId,
            offer_url: ofertaUrl,
            action: "check" as const,
            website_address: window.location.href,
            checkbox_text: checkboxText,
          },
        ];


        await postToggleOfferCheckbox(checkboxes);
      } else {
        const unchecked = [{ questionnaire_uid: clientId, offer_url: ofertaUrl, action: "uncheck" as const }];
        await postToggleOfferCheckbox(unchecked);
      }
    } catch {
    } finally {
      setIsPendingOffer(false);
    }
  };

  return (
    <div className={cn(styles.container, containerClassName)}>
      <FormField name="accept-politics" renderError={acceptPoliticsError}>
        <div style={{ position: "relative", pointerEvents: isPendingOffer ? "none" : "auto" }}>
          {isPendingOffer && (
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 16, color: "#3d935e" }} spin />}
              style={{ position: "absolute", left: 0, top: 10, zIndex: 1 }}
            />
          )}
          <CheckboxField
            fieldName="accept-politics"
            checked={isAcceptPolitics}
            title={politicsTitle}
            styleIndicatorProps={{ top: 4, visibility: isPendingOffer ? "hidden" : undefined }}
            onChange={handleAcceptPoliticsChange}
            disabled={isPendingOffer}
          />
        </div>
      </FormField>

      <FormField name="accept-privacy" renderError={acceptPrivacyError}>
        <CheckboxField
          fieldName="accept-privacy"
          checked={isAcceptPrivacy}
          title={<CustomerAcceptsPrivacyTitle />}
          styleIndicatorProps={{ top: 4 }}
          onChange={(val: boolean) => {
            if (!isAcceptPrivacy) setAcceptPrivacyError(null);
            setIsAcceptPrivacy(val);
          }}
        />
      </FormField>

      <FormField name="accept_promo" renderError={acceptPromoError}>
        <CheckboxField
          fieldName="accept_promo"
          checked={isAcceptPromo}
          title={<CustomerAcceptPromoTitle />}
          styleIndicatorProps={{ top: 4 }}
          onChange={(val: boolean) => {
            if (!isAcceptPromo) setAcceptPromoError(null);
            setIsAcceptPromo(val);
          }}
        />
      </FormField>
    </div>
  );
};
